import { breakpoints } from '../../../resources/scripts/config/breakpoints';

const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
const isFirefox = navigator.userAgent.toLowerCase().indexOf('firefox') > -1;

const initTabManager = (container) => {
  const tabs = Array.from(container.querySelectorAll('.complex-video__tab'));
  const panels = Array.from(container.querySelectorAll('.complex-video__content-items'));
  let isFirstClick = true;

  const resetPanelState = (panel, isMobile) => {
    const items = Array.from(panel.querySelectorAll('.complex-video__content-item'));

    if (isMobile) {
      panel.classList.remove('complex-video__content-items--mobile-active');
      items.forEach((item) => {
        item.classList.remove('complex-video__content-item--active');
        item.classList.remove('complex-video__content-item--side-left');
        item.classList.remove('complex-video__content-item--side-right');
        item.style.removeProperty('--side-index');
      });
    } else {
      panel.classList.remove('complex-video__content-items--desktop-active');
      panel.style.removeProperty('--total-side-items');
      items.forEach((item) => {
        item.classList.remove('complex-video__content-item--active-desktop');
        item.classList.remove('complex-video__content-item--side-desktop');
        item.style.removeProperty('--desktop-index');
      });
    }

    items.forEach((item) => {
      const video = item.querySelector('.complex-video__content-media');
      if (video) {
        video.pause();
        video.removeAttribute('controls');
        video.removeAttribute('width');
        video.removeAttribute('height');
      }
    });
  };

  const switchTab = (activeTab) => {
    if (!activeTab || !(activeTab instanceof HTMLElement)) return;

    if (activeTab.classList.contains('complex-video__tab--active') && !isFirstClick) {
      return;
    }

    if (isFirstClick) {
      isFirstClick = false;
    }

    tabs.forEach((tab) => {
      tab.classList.remove('complex-video__tab--active');
      tab.setAttribute('aria-selected', 'false');
      // eslint-disable-next-line no-param-reassign
      tab.tabIndex = -1;
      // eslint-disable-next-line no-param-reassign
      tab.style.opacity = '0.5';

      const parentItem = tab.closest('.complex-video__tabs-item');
      if (parentItem) {
        parentItem.classList.remove('complex-video__tabs-item--user-selected');
      }
    });

    panels.forEach((panel) => {
      // eslint-disable-next-line no-param-reassign
      panel.hidden = true;
      const isMobile = window.innerWidth < breakpoints.lg;
      resetPanelState(panel, isMobile);
    });

    activeTab.classList.add('complex-video__tab--active');
    activeTab.setAttribute('aria-selected', 'true');
    // eslint-disable-next-line no-param-reassign
    activeTab.tabIndex = 0;
    // eslint-disable-next-line no-param-reassign
    activeTab.style.opacity = '1';
    const parentItem = activeTab.closest('.complex-video__tabs-item');

    if (parentItem) {
      parentItem.classList.add('complex-video__tabs-item--user-selected');
    }

    const targetId = activeTab.getAttribute('aria-controls');
    if (!targetId) return;

    const targetPanel = container.querySelector(`#${CSS.escape(targetId)}`);
    if (targetPanel) {
      targetPanel.hidden = false;
    }
  };

  const handleKeyboard = (key, currentTab) => {
    const currentIndex = tabs.indexOf(currentTab);
    if (currentIndex === -1) return;
    let nextIndex = currentIndex;

    switch (key) {
      case 'ArrowRight':
        nextIndex = (currentIndex + 1) % tabs.length;
        break;
      case 'ArrowLeft':
        nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = tabs.length - 1;
        break;
      default:
        break;
    }

    tabs[nextIndex].focus();
    switchTab(tabs[nextIndex]);
  };

  container.addEventListener('click', (e) => {
    const tab = e.target.closest('.complex-video__tab');
    if (tab) switchTab(tab);
  });

  container.addEventListener('keydown', (e) => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return;

    const currentTab = e.target.closest('.complex-video__tab');
    if (!currentTab) return;

    e.preventDefault();
    handleKeyboard(e.key, currentTab);
  });
};

const initDesktopVideoManager = (container) => {
  const panels = Array.from(container.querySelectorAll('.complex-video__content-items'));

  const selectDesktopVideo = (panel, selectedItem, selectedIndex, allItems) => {
    if (window.innerWidth < breakpoints.lg) return;
    if (!selectedItem) return;

    const isAlreadyActive = selectedItem.classList.contains('complex-video__content-item--active-desktop');

    if (isAlreadyActive) {
      const selectedVideo = selectedItem.querySelector('.complex-video__content-media');
      if (selectedVideo) {
        if (selectedVideo.paused) {
          selectedVideo.play();
        } else {
          selectedVideo.pause();
        }
      }
      return;
    }

    allItems.forEach((item) => {
      item.classList.remove('complex-video__content-item--active-desktop');
      item.classList.remove('complex-video__content-item--side-desktop');
      item.style.removeProperty('--desktop-index');

      const video = item.querySelector('.complex-video__content-media');
      if (video) {
        video.pause();
        video.removeAttribute('controls');
        video.removeAttribute('width');
        video.removeAttribute('height');
      }
    });

    selectedItem.classList.add('complex-video__content-item--active-desktop');

    const selectedVideo = selectedItem.querySelector('.complex-video__content-media');
    if (selectedVideo) {
      selectedVideo.setAttribute('width', '300');
      selectedVideo.setAttribute('height', '400');
      selectedVideo.setAttribute('controls', 'controls');
      selectedVideo.play();
    }

    const otherItems = allItems.filter((_, index) => index !== selectedIndex);

    otherItems.forEach((item, index) => {
      item.classList.add('complex-video__content-item--side-desktop');
      item.style.setProperty('--desktop-index', index);
    });

    panel.style.setProperty('--total-side-items', otherItems.length);
    panel.classList.add('complex-video__content-items--desktop-active');
  };

  const resetDesktopState = () => {
    if (window.innerWidth < breakpoints.lg) {
      panels.forEach((panel) => {
        panel.classList.remove('complex-video__content-items--desktop-active');
        panel.style.removeProperty('--total-side-items');
        const items = Array.from(panel.querySelectorAll('.complex-video__content-item'));
        items.forEach((item) => {
          item.classList.remove('complex-video__content-item--active-desktop');
          item.classList.remove('complex-video__content-item--side-desktop');
          item.style.removeProperty('--desktop-index');

          const video = item.querySelector('.complex-video__content-media');
          if (video) {
            video.pause();
            video.removeAttribute('controls');
            video.removeAttribute('width');
            video.removeAttribute('height');
          }
        });
      });
    }
  };

  panels.forEach((panel) => {
    const items = Array.from(panel.querySelectorAll('.complex-video__content-item'));

    items.forEach((item, index) => {
      item.addEventListener('click', (e) => {
        if (window.innerWidth >= breakpoints.lg) {
          if (isSafari) {
            const clickedVideo = e.target.closest('.complex-video__content-media');
            const isVideoActive = item.classList.contains('complex-video__content-item--active-desktop');
            if (clickedVideo && clickedVideo.hasAttribute('controls') && isVideoActive) {
              return;
            }
          }
          e.preventDefault();
          selectDesktopVideo(panel, item, index, items);
        }
      });
    });
  });

  window.addEventListener('resize', resetDesktopState);
};

const initMobileVideoManager = (container) => {
  const panels = Array.from(container.querySelectorAll('.complex-video__content-items'));

  const selectMobileVideo = (panel, selectedItem, selectedIndex, allItems) => {
    if (window.innerWidth >= breakpoints.lg) return;
    if (!selectedItem) return;

    const isAlreadyActive = selectedItem.classList.contains('complex-video__content-item--active');

    if (isAlreadyActive) {
      const selectedVideo = selectedItem.querySelector('.complex-video__content-media');
      if (selectedVideo) {
        if (selectedVideo.paused) {
          selectedVideo.play();
        } else {
          selectedVideo.pause();
        }
      }
      return;
    }

    allItems.forEach((item) => {
      item.classList.remove('complex-video__content-item--active');
      item.classList.remove('complex-video__content-item--side-left');
      item.classList.remove('complex-video__content-item--side-right');
      item.style.removeProperty('--side-index');

      const video = item.querySelector('.complex-video__content-media');
      if (video) {
        video.pause();
        video.removeAttribute('controls');
        video.removeAttribute('width');
        video.removeAttribute('height');
      }
    });

    selectedItem.classList.add('complex-video__content-item--active');

    const selectedVideo = selectedItem.querySelector('.complex-video__content-media');
    if (selectedVideo) {
      selectedVideo.setAttribute('width', '250');
      selectedVideo.setAttribute('height', '338');
      selectedVideo.setAttribute('controls', 'controls');
      selectedVideo.play();
    }

    const itemsBeforeCount = selectedIndex;
    let rightIndex = 0;

    allItems.forEach((item, index) => {
      if (index < selectedIndex) {
        item.classList.add('complex-video__content-item--side-left');
        const reversedIndex = itemsBeforeCount - 1 - index;
        item.style.setProperty('--side-index', reversedIndex);
      } else if (index > selectedIndex) {
        item.classList.add('complex-video__content-item--side-right');
        item.style.setProperty('--side-index', rightIndex);
        rightIndex++;
      }
    });

    panel.classList.add('complex-video__content-items--mobile-active');
  };

  const resetMobileState = () => {
    if (window.innerWidth >= breakpoints.lg) {
      panels.forEach((panel) => {
        panel.classList.remove('complex-video__content-items--mobile-active');
        const items = Array.from(panel.querySelectorAll('.complex-video__content-item'));
        items.forEach((item) => {
          item.classList.remove('complex-video__content-item--active');
          item.classList.remove('complex-video__content-item--side-left');
          item.classList.remove('complex-video__content-item--side-right');
          item.style.removeProperty('--side-index');

          const video = item.querySelector('.complex-video__content-media');
          if (video) {
            video.pause();
            video.removeAttribute('controls');
            video.removeAttribute('width');
            video.removeAttribute('height');
          }
        });
      });
    }
  };

  panels.forEach((panel) => {
    const items = Array.from(panel.querySelectorAll('.complex-video__content-item'));

    items.forEach((item, index) => {
      item.addEventListener('click', (e) => {
        if (window.innerWidth < breakpoints.lg) {
          if (isSafari) {
            const clickedVideo = e.target.closest('.complex-video__content-media');
            if (clickedVideo && clickedVideo.hasAttribute('controls')) {
              return;
            }
          }
          e.preventDefault();
          selectMobileVideo(panel, item, index, items);
        }
      });
    });
  });

  window.addEventListener('resize', resetMobileState);
};

const initVideoHoverEffects = () => {
  const videos = Array.from(document.querySelectorAll('.complex-video__content-media'));

  const applyHoverListeners = (video) => {
    if (!isFirefox) {
      video.addEventListener('mouseenter', () => {
        if (window.innerWidth >= breakpoints.lg) {
          const panel = video.closest('.complex-video__content-items');

          if (panel && !panel.classList.contains('complex-video__content-items--desktop-active')) {
            video.play();
            video.setAttribute('controls', 'controls');
          }
        }
      });

      video.addEventListener('mouseleave', () => {
        if (window.innerWidth >= breakpoints.lg) {
          const panel = video.closest('.complex-video__content-items');

          if (panel && !panel.classList.contains('complex-video__content-items--desktop-active')) {
            video.pause();
            video.removeAttribute('controls');
          }
        }
      });
    }

    if (window.innerWidth < breakpoints.lg) {
      video.removeAttribute('controls');
    }
  };

  videos.forEach(applyHoverListeners);

  window.addEventListener('resize', () => {
    Array.from(document.querySelectorAll('.complex-video__content-media')).forEach((video) => {
      if (window.innerWidth < breakpoints.lg) {
        video.removeAttribute('controls');
      } else if (video.paused) {
        video.removeAttribute('controls');
      }
    });
  });
};

const initFullscreenHandlers = () => {
  const queryVideos = () => Array.from(document.querySelectorAll('.complex-video__content-media'));

  const getFullscreenElement = () => document.fullscreenElement
    || document.webkitFullscreenElement
    || document.mozFullScreenElement
    || document.msFullscreenElement
    || null;

  const restoreControlsForActive = (delay = 50) => {
    setTimeout(() => {
      queryVideos().forEach((video) => {
        const item = video.closest('.complex-video__content-item');
        if (!item) return;

        const isActive = item.classList.contains('complex-video__content-item--active')
          || item.classList.contains('complex-video__content-item--active-desktop');

        if (isActive) {
          video.setAttribute('controls', 'controls');
          // eslint-disable-next-line no-param-reassign
          video.controls = true;
        }
      });
    }, delay);
  };

  const onFullscreenChange = () => {
    const fsEl = getFullscreenElement();
    if (!fsEl) {
      restoreControlsForActive(50);
    }
  };

  document.addEventListener('fullscreenchange', onFullscreenChange);
  document.addEventListener('webkitfullscreenchange', onFullscreenChange);
  document.addEventListener('mozfullscreenchange', onFullscreenChange);
  document.addEventListener('MSFullscreenChange', onFullscreenChange);

  queryVideos().forEach((video) => {
    video.addEventListener('webkitendfullscreen', () => {
      restoreControlsForActive(120);
    });

    video.addEventListener('webkitbeginfullscreen', () => { });
  });
};

const complexVideo = () => {
  document.querySelectorAll('.complex-video__content-media').forEach((video) => {
    video.removeAttribute('controls');
  });

  document.querySelectorAll('.complex-video').forEach((container) => {
    if (isSafari) {
      container.classList.add('is-safari');
    }

    initTabManager(container);
    initDesktopVideoManager(container);
    initMobileVideoManager(container);
  });

  initVideoHoverEffects();
  initFullscreenHandlers();

  document.querySelectorAll('.complex-video__content-media').forEach((video) => {
    if (window.innerWidth < breakpoints.lg) {
      video.removeAttribute('controls');
    }
  });
};

export default complexVideo;
